import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Camera, X } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";

interface CameraAccess {
  id: string;
  userId: string;
  accessLevel: string;
  grantedAt: string;
  revokedAt?: string | null;
  notes?: string | null;
  user: { firstName: string; lastName: string; email: string; role: string };
  grantedBy: { firstName: string; lastName: string };
}

const ACCESS_LEVELS = ["Lobby", "Áreas Comunes", "Todas las Cámaras", "Sólo Lectura"];

export function CamerasPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CameraAccess | null>(null);

  const { data: access = [], isLoading, isError } = useQuery({
    queryKey: ["camera-access"],
    queryFn: async () => (await api.get<CameraAccess[]>("/camera-access")).data,
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/camera-access/${id}/revoke`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["camera-access"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/camera-access/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["camera-access"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Camera size={24} /> {t("security.cameras")}
        </h1>
        <button onClick={() => setOpen(true)} className="btn-primary">
          <Plus size={16} /> {t("security.grantAccess")}
        </button>
      </div>

      <div className="card p-0 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading ? (
          <div className="p-6 text-slate-500">{t("common.loading")}</div>
        ) : access.length === 0 ? (
          <div className="p-6 text-slate-500">—</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">{t("security.user")}</th>
                <th className="text-left px-4 py-2">{t("security.accessLevel")}</th>
                <th className="text-left px-4 py-2">Otorgado</th>
                <th className="text-left px-4 py-2">{t("security.notes")}</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {access.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <div className="font-medium">{a.user.firstName} {a.user.lastName}</div>
                    <div className="text-xs text-slate-500">{a.user.email}</div>
                  </td>
                  <td className="px-4 py-2"><Badge variant="info">{a.accessLevel}</Badge></td>
                  <td className="px-4 py-2">
                    {formatDate(a.grantedAt, i18n.language)}
                    <div className="text-xs text-slate-500">por {a.grantedBy.firstName} {a.grantedBy.lastName}</div>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{a.notes ?? "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex gap-1 items-center justify-end">
                      <button className="btn bg-red-50 text-red-700 hover:bg-red-100 text-xs" onClick={() => revokeMutation.mutate(a.id)}>
                        <X size={12} /> {t("security.revokeAccess")}
                      </button>
                      <AdminRecordActions
                        compact
                        entityLabel={`${a.user.firstName} ${a.user.lastName}`}
                        onEdit={() => setEditing(a)}
                        onDelete={() => deleteMutation.mutate(a.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <GrantModal
        open={open || !!editing}
        editing={editing}
        onClose={() => { setOpen(false); setEditing(null); }}
        onDone={() => qc.invalidateQueries({ queryKey: ["camera-access"] })}
      />
    </div>
  );
}

function GrantModal({ open, editing, onClose, onDone }: { open: boolean; editing?: CameraAccess | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const { data: users = [] } = useQuery({
    queryKey: ["users-list"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: open && !editing,
  });
  const [form, setForm] = useState({ userId: "", accessLevel: ACCESS_LEVELS[0], notes: "" });

  useEffect(() => {
    if (editing) {
      setForm({ userId: editing.userId, accessLevel: editing.accessLevel, notes: editing.notes ?? "" });
    } else {
      setForm({ userId: "", accessLevel: ACCESS_LEVELS[0], notes: "" });
    }
  }, [editing]);

  const mutation = useMutation({
    mutationFn: async () =>
      editing
        ? (await api.put(`/camera-access/${editing.id}`, { accessLevel: form.accessLevel, notes: form.notes || null })).data
        : (await api.post("/camera-access", { ...form, notes: form.notes || null })).data,
    onSuccess: () => {
      onDone();
      onClose();
      setForm({ userId: "", accessLevel: ACCESS_LEVELS[0], notes: "" });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={editing ? t("security.cameras") : t("security.grantAccess")}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <div>
          <label className="label">{t("security.user")}</label>
          {editing ? (
            <div className="input bg-slate-50 text-slate-600">{editing.user.firstName} {editing.user.lastName}</div>
          ) : (
            <select className="input" required value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })}>
              <option value="">—</option>
              {(users as any[]).map((u) => (
                <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.role})</option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="label">{t("security.accessLevel")}</label>
          <select className="input" value={form.accessLevel} onChange={(e) => setForm({ ...form, accessLevel: e.target.value })}>
            {ACCESS_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="label">{t("security.notes")}</label>
          <input className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}
